import { FC } from 'react'
import { Box, Paper, useTheme } from '@mui/material'
import { useAzureApplicationOwnersAndDeputies, useResponsibleParty } from 'hooks'
import { Skeleton } from '@mui/material'
import { ApplicationOwnerDeputies } from './ApplicationsOwnerDeputies'
import { useTranslation } from 'react-i18next'
import useSubcomponents from 'useSubcomponents'
import { PersonDetails } from 'components/ApplicationBanner/PersonDetails'
import { useIsSmallScreen } from 'packages/core'
import { ItemDetails } from 'components'

export type ApplicationAdditionalInformationProps = {
    className?: string
    buttonPosition?: 'right' | 'bottom'
    data: any
    applicationId: string
    isAzureApplication: boolean
}
const smallScreenWidth = 960

const hasAccessToOwnerControl = 'ITShop-Applications-Details-Owners-Control'
const hasAccessToADeputiesControl =
    'ITShop-Applications-Details-Deputies-Control'
const hasAccessToResponsiblePartyControl =
    'ITShop-Applications-Details-ResponsibleParty-Control'


export const ApplicationAdditionalInformation: FC<
    ApplicationAdditionalInformationProps
> = (props) => {
    const { t } = useTranslation()
    const theme = useTheme()
    const { hasAccessToControl } = useSubcomponents()
    const { data: ownersAndDeputies, isInitialLoading } =
        useAzureApplicationOwnersAndDeputies(
            hasAccessToControl(hasAccessToOwnerControl) ||
                hasAccessToControl(hasAccessToADeputiesControl)
                ? props.applicationId
                : null,
        )

    const attributeOrientation = useIsSmallScreen() ? 'vertical' : 'horizontal'

    const {
        data: responsibleParty,
        isInitialLoading: responsiblePartyLoading,
    } = useResponsibleParty(
        props.applicationId,
        Boolean(hasAccessToControl(hasAccessToResponsiblePartyControl)),
    )

    return (
        <Paper
            sx={{
                position: 'relative',
                padding: theme.spacing(2),
                display: 'flex',
                miHeight: '156px',
                justifyContent: 'space-between',
                boxShadow: 'none !important',

                [`@media (max-width:${smallScreenWidth - 1}px)`]: {
                    flexDirection: 'column',
                },
            }}
            data-ai-explain="Application more information: owners, responsible party, Azure deputies when applicable, and governance contacts for this catalog item."
        >
            <Box
                sx={{
                    padding: '0 20px',
                    width: '100%',
                }}
            >
                {isInitialLoading || responsiblePartyLoading ? (
                    <Box
                        paddingRight={4}
                        data-ai-explain="Loading owners, deputies, and responsible party for this application."
                    >
                        <Skeleton height={40} />
                    </Box>
                ) : (
                    <>
                        {hasAccessToControl(hasAccessToOwnerControl) && (
                            <Box
                                data-protectedsubcomponent={
                                    hasAccessToOwnerControl
                                }
                                data-ai-explain="Owners of this catalog application."
                            >
                                <ApplicationOwnerDeputies
                                    label={t('Common_Owners')}
                                    data={ownersAndDeputies.owners}
                                />
                            </Box>
                        )}
                        {hasAccessToControl(
                            hasAccessToResponsiblePartyControl,
                        ) && (
                            <Box data-ai-explain="Responsible party or primary owner contact for this application.">
                                <ItemDetails.Attribute
                                    label={t('Applications_Owner')}
                                    value={
                                        responsibleParty ? (
                                            <PersonDetails
                                                size="medium"
                                                person={{
                                                    imageThumbUrl:
                                                        responsibleParty.imageThumbUrl,
                                                    friendlyName:
                                                        responsibleParty.friendlyName,
                                                    email: responsibleParty.email,
                                                    telephone:
                                                        responsibleParty.telephone,
                                                }}
                                            />
                                        ) : (
                                            <>-</>
                                        )
                                    }
                                    orientation={attributeOrientation}
                                />
                            </Box>
                        )}
                        {props.isAzureApplication && (
                            <>
                                {hasAccessToControl(
                                    hasAccessToADeputiesControl,
                                ) && (
                                        <Box
                                            data-protectedsubcomponent={
                                                hasAccessToADeputiesControl
                                            }
                                            data-ai-explain="Deputy contacts for this Azure application."
                                        >
                                            <ApplicationOwnerDeputies
                                                label={t('Common_Deputies')}
                                                data={ownersAndDeputies.deputies}
                                            />
                                        </Box>
                                    )}
                            </>
                        )}
                    </>
                )}
            </Box>
        </Paper>
    )
}
